import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Clock, AlertTriangle, Sparkles } from "lucide-react";
import { Header, SectionLabel } from "../components/Brand";

export default function LandingPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Brandhero × HBTU — Buyer Conviction Sprint";
  }, []);

  return (
    <div className="min-h-screen bg-[#09090b] text-white" data-testid="landing-page">
      <Header
        right={
          <button
            data-testid="admin-link-btn"
            onClick={() => navigate("/admin")}
            className="text-xs uppercase tracking-[0.2em] text-zinc-300 hover:text-[#ff4950] transition-colors"
          >
            Admin
          </button>
        }
      />

      <section className="relative overflow-hidden">
        <div className="bh-grid-bg absolute inset-0 opacity-40 pointer-events-none" />
        <div className="bg-grain absolute inset-0 opacity-30 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 pb-24 lg:pt-32 lg:pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8 bh-fade-up">
              <SectionLabel className="mb-6">
                Brandhero × HBTU Campus Challenge
              </SectionLabel>
              <h1 className="font-display font-black text-4xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tighter">
                Start your
                <br />
                <span className="text-[#ff4950]">Buyer Conviction</span>
                <br />
                <span className="bh-outline-text">Sprint.</span>
              </h1>
              <p className="mt-8 max-w-xl text-zinc-200 text-base sm:text-lg leading-relaxed">
                The AI Growth Systems Intern assignment. You have{" "}
                <span className="text-white font-semibold">4 hours 30 minutes</span>{" "}
                from the moment you press start. No pauses. No resets.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <button
                  data-testid="start-assignment-cta"
                  onClick={() => navigate("/start")}
                  className="bh-btn-primary group"
                >
                  Start Assignment Now
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <p className="text-xs text-zinc-400 max-w-xs">
                  Please start only when you are fully ready to work on the assignment.
                </p>
              </div>
            </div>

            <div className="lg:col-span-4 lg:col-start-9 bh-fade-up" style={{ animationDelay: "120ms" }}>
              <div className="border border-[var(--bh-accent-border)] bg-[var(--bh-accent-soft)] p-6 rounded-sm">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-[#ff4950] mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[0.7rem] uppercase tracking-[0.2em] text-[#ff4950] font-bold mb-2">
                      One-shot timer
                    </div>
                    <p className="text-sm text-zinc-100 leading-relaxed">
                      Once you click <span className="text-white font-semibold">Start Assignment Now</span>,
                      your timer will begin and{" "}
                      <span className="text-[#ff4950] font-semibold">cannot be reset</span>.
                      Closing the browser does not pause the timer.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 lg:py-24">
          <SectionLabel className="mb-8">The Brief, in one screen</SectionLabel>
          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              {
                tag: "01",
                title: "Analyse a B2B brand",
                body: "Identify buyer trust gaps. Pinpoint where conviction breaks for the actual decision maker.",
                icon: Sparkles,
              },
              {
                tag: "02",
                title: "Build the system",
                body: "A Buyer Conviction Score. A Google Sheet audit system. Document your structured thinking.",
                icon: Clock,
              },
              {
                tag: "03",
                title: "Ship in 4.5h",
                body: "Submit Docs, Sheet, and a 90-second video link. AI-assisted is welcome — just tell us how.",
                icon: ArrowRight,
              },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.tag} className="bg-[#09090b] p-8">
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-display font-black text-3xl text-zinc-600">
                      {p.tag}
                    </span>
                    <Icon className="w-5 h-5 text-[#ff4950]" />
                  </div>
                  <h3 className="font-display font-bold text-xl mb-3 text-white">
                    {p.title}
                  </h3>
                  <p className="text-sm text-zinc-300 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <SectionLabel className="mb-3">AI Growth Systems Intern</SectionLabel>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-white">
              Ready when you are.
            </h2>
          </div>
          <button
            data-testid="start-assignment-cta-bottom"
            onClick={() => navigate("/start")}
            className="bh-btn-primary"
          >
            Start the Sprint
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-xs text-zinc-500 flex flex-col sm:flex-row justify-between gap-3">
          <span>© 2026 Brandhero — AI Growth Studio.</span>
          <span className="uppercase tracking-[0.2em]">HBTU Campus Challenge</span>
        </div>
      </footer>
    </div>
  );
}
